// -----------------------------------------------------------------------------
// <copyright file="login.service.ts" company="ecoInsight, Inc.">
// Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoaderService } from '../../shared/loader/loader.service';
import { GlobalService } from '../../shared/services/global.service';
import { HttpHelperService } from '../../shared/services/http.service';
import { Observable, of } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';
import { VcDataService } from '../../features/configurator/service/vc-data';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly loader = inject(LoaderService);
  private readonly globalService = inject(GlobalService);
  private readonly vcDataService = inject(VcDataService);
  private readonly http = inject(HttpHelperService);
  private readonly router = inject(Router);

  private lookupDataFetching = false;
  private lookupDataFetched = false;

  constructor() {
    this.checkLookupDataState();
  }

  private checkLookupDataState(): void {
    if (sessionStorage.getItem('lookup_data_fetched_in_session')) {
      this.lookupDataFetched = true;
    }
  }

  navigateToProjectList(enableAutoRoute = true): void {
    this.loader.setLoadingState.set(true);
    if (enableAutoRoute) {
      const redirectUrl = localStorage.getItem('vas_redirect_url');
      if (redirectUrl) {
        localStorage.removeItem('vas_redirect_url');
        this.router.navigateByUrl(redirectUrl);
      } else {
        if (!this.globalService.isBusinessUnitSelected()) {
          this.globalService.appInitialized.set(true);
          this.router.navigate(['select-business-unit']);
          return;
        } else {
          this.getUserDetails().subscribe((res) => {
            this.storeUserDetails(res.Result);
          });
        }
        this.globalService.navigateToHomeByView();
      }
    }
    this.globalService.appInitialized.set(true);
  }

  storeUserDetails(userData: any): void {
    localStorage.setItem('selected-business-unit', userData.BusinessUnitId);
    localStorage.setItem('app_user_details', JSON.stringify(userData));
    sessionStorage.setItem('app_user_details', JSON.stringify(userData));
    this.globalService.setCurrency(JSON.parse(userData?.CurrencyJson).currencySymbols);
  }

  fetchAndCacheLookupData(): Observable<any> {
    if (this.lookupDataFetched) {
      return of({ cached: true });
    }
    if (sessionStorage.getItem('lookup_data_fetched_in_session')) {
      this.lookupDataFetched = true;
      return of({ cached: true });
    }
    if (this.lookupDataFetching) {
      return of({ fetching: true });
    }
    this.lookupDataFetching = true;
    return this.globalService.lookupDataListCategories().pipe(
      tap(
        (res) => {
          this.cacheLookupData(res);
          this.markLookupDataFetched();
        },
        (error) => {
          this.lookupDataFetching = false;
          console.error('Error fetching lookup data:', error);
        },
      ),
    );
  }

  getUserDetails() {
    return this.http.get('api/GetUserDetails');
  }

  getBusinessUnits() {
    return this.http.get('api/GetBusinessUnits');
  }

  private cacheLookupData(res: any): void {
    localStorage.setItem('user_list', JSON.stringify(res.BusinessUnitUsers));
    localStorage.setItem('ship_location_list', JSON.stringify(res.ShipLocations));
    localStorage.setItem('ddp_ship_location_list', JSON.stringify(res.DDPShipLocations));
    localStorage.setItem('SALES_LOCATION_LIST', JSON.stringify(res.SalesLocations));
    const typeCodeDescriptions = res.TypeCodeDescriptions;
    localStorage.setItem('type_code_descriptions', JSON.stringify(typeCodeDescriptions));
    localStorage.setItem(
      'instruction_type_list',
      JSON.stringify(
        typeCodeDescriptions.filter(
          (item: any) => item.Entity === 'WorkInstructionLineItem.InstructionType',
        ),
      ),
    );
    localStorage.setItem(
      'lineitem_status_list',
      JSON.stringify(
        typeCodeDescriptions.filter(
          (item: any) => item.Entity === 'SalesOrderWorkInstructionLineItem.LineItemStatus',
        ),
      ),
    );
    localStorage.setItem(
      'lineitem_shipType_list',
      JSON.stringify(
        typeCodeDescriptions.filter((item: any) => item.Entity === 'FinishedGoodLineItem.ShipType'),
      ),
    );
    localStorage.setItem(
      'lineitem_InventoryType_list',
      JSON.stringify(
        typeCodeDescriptions.filter(
          (item: any) => item.Entity === 'FinishedGoodLineItem.InventoryType',
        ),
      ),
    );
    localStorage.setItem(
      'uom_list',
      JSON.stringify(
        typeCodeDescriptions.filter((item: any) => item.Entity === 'FinishedGood.UOM'),
      ),
    );
    localStorage.setItem(
      'work-order-release-list',
      JSON.stringify(
        typeCodeDescriptions.filter((item: any) => item.Entity === 'FinishedGood.WorkOrderRelease'),
      ),
    );

    // update here when setting the values to Map
    this.vcDataService.setShipLocations(res.ShipLocations);
    this.vcDataService.setSalesLocations(res.SalesLocations);
  }

  private markLookupDataFetched(): void {
    localStorage.setItem('lookup_data_fetched_at', Date.now().toString());
    sessionStorage.setItem('lookup_data_fetched_in_session', 'true');
    this.lookupDataFetching = false;
    this.lookupDataFetched = true;
  }

  resetLookupDataState(): void {
    this.lookupDataFetched = false;
    this.lookupDataFetching = false;
    sessionStorage.removeItem('lookup_data_fetched_in_session');
    localStorage.removeItem('type_code_descriptions');
  }

  getOrFetchLookupData(): Observable<any> {
    return this.fetchAndCacheLookupData().pipe(switchMap(() => of({ success: true })));
  }
}
